import { useState, useEffect, useMemo } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import { supabase } from '../../lib/supabaseClient';
import { RequirementDetail } from '../Requirements/Requirements';
import './RequirementsDashboard.css';

/* Statuses considered "closed/cancelled" — excluded from this view. */
const CLOSED_STATUS_IDS = [4, 14]; // Cancelado, Entregado

/* Internal-status colors the PO wants shown in this view: green (#92d050)
   and sky blue (#a6c9ec) are the ONLY statuses that qualify as "real work".
   Filtering by COLOR (not status id) intentionally covers every status
   sharing those colors and survives recoloring. The same set drives the
   "sin trabajo real" tally below. Compared case-insensitively. */
const VISIBLE_STATUS_COLORS = new Set(['#92d050', '#a6c9ec']);

/* ── Icons ───────────────────────────────────────── */
function IconUserCircle() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="10" r="3.5" />
      <path d="M5.5 19a7 7 0 0 1 13 0" />
    </svg>
  );
}

function IconBriefcase() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconStack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconInbox() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

/* ── Helpers ─────────────────────────────────────── */
function getContrastColor(hex) {
  if (!hex) return '#222222';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#222222' : '#FFFFFF';
}

function initials(fullName) {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/* Deterministic colour pick per developer for the avatar accent. */
const AVATAR_PALETTE = [
  '#00115D', '#00CFC1', '#2E0055', '#215c98', '#5B2A86',
  '#0A6E7A', '#1F3A93', '#067A5C', '#7A2E68', '#1A5276',
];
function avatarColor(id) {
  if (!id) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function clampPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/* ── Requirement entry (clickable → full detail) ─── */
function RequirementEntry({ req, onSelect }) {
  const status = req.status_rel;
  const project = req.project;
  const client = project?.client;
  const pct = clampPct(req.progress_percentage);

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(req.id);
    }
  }

  return (
    <li
      className="rqd-req-item rqd-req-item--clickable"
      role="button"
      tabIndex={0}
      aria-label={`Ver detalle de ${req.reference_id}`}
      onClick={() => onSelect(req.id)}
      onKeyDown={handleKeyDown}
    >
      <div className="rqd-req-row1">
        <span className="rqd-req-folio" title={req.reference_id}>{req.reference_id}</span>
        {status && (
          <span
            className="rqd-status-badge"
            style={{ backgroundColor: status.color, color: getContrastColor(status.color) }}
          >
            {status.name}
          </span>
        )}
      </div>

      <div className="rqd-req-meta">
        <span className="rqd-meta-chip" title={client?.name || 'Sin cliente'}>
          <IconBriefcase />
          <span className="rqd-meta-text">{client?.name || '—'}</span>
        </span>
        <span className="rqd-meta-chip" title={project?.name || 'Sin proyecto'}>
          <IconFolder />
          <span className="rqd-meta-text">{project?.name || '—'}</span>
        </span>
      </div>

      <div className="rqd-progress-row">
        <div className="rqd-progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="rqd-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="rqd-progress-pct">{pct}%</span>
      </div>
    </li>
  );
}

/* ── Developer card ──────────────────────────────── */
function DeveloperCard({ dev, requirements, index, onSelectReq }) {
  const count = requirements.length;
  const accent = avatarColor(dev.id);
  const roleName = dev.roles?.name || 'Desarrollador';
  /* Same "real work" definition used for the page-level tally and the sort
     order — Líderes are never flagged, regardless of their count. */
  const noRealWork = roleName !== 'Líder' && count === 0;

  return (
    <article
      className={`rqd-dev-card${noRealWork ? ' rqd-dev-card--no-work' : ''}`}
      style={{ '--delay': `${index * 60}ms`, '--accent': accent }}
    >
      <header className="rqd-dev-header">
        <div className="rqd-avatar" aria-hidden="true">
          <span className="rqd-avatar-initials">{initials(dev.full_name)}</span>
        </div>
        <div className="rqd-dev-id">
          <h2 className="rqd-dev-name">{dev.full_name}</h2>
          <span className="rqd-dev-role">
            <IconUserCircle />
            {roleName}
          </span>
        </div>
        <span className={`rqd-count-badge${count === 0 ? ' rqd-count-badge--empty' : ''}`}>
          {count}
        </span>
      </header>

      {count === 0 ? (
        <div className="rqd-empty-card">
          <IconInbox />
          <p>Sin requerimientos activos asignados</p>
        </div>
      ) : (
        <ul className="rqd-req-list">
          {requirements.map((r) => (
            <RequirementEntry key={r.id} req={r} onSelect={onSelectReq} />
          ))}
        </ul>
      )}
    </article>
  );
}

/* ── Skeleton card ──────────────────────────────── */
function SkeletonCard({ index }) {
  return (
    <article className="rqd-dev-card rqd-skeleton-card" style={{ '--delay': `${index * 60}ms` }}>
      <header className="rqd-dev-header">
        <div className="rqd-skel rqd-skel--avatar" />
        <div className="rqd-skel-stack">
          <div className="rqd-skel rqd-skel--line rqd-skel--w60" />
          <div className="rqd-skel rqd-skel--line rqd-skel--w40" />
        </div>
      </header>
      <div className="rqd-skel rqd-skel--block" />
      <div className="rqd-skel rqd-skel--block" />
    </article>
  );
}

/* ── Page component ─────────────────────────────── */
function RequirementsDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devs, setDevs] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [selectedReqId, setSelectedReqId] = useState(null);

  /* ── Current user role (gates client-facing fields in the detail view) ── */
  useEffect(() => {
    let cancelled = false;
    async function fetchRole() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('users')
        .select('roles(name)')
        .eq('id', user.id)
        .single();
      if (!cancelled) setUserRole(data?.roles?.name ?? null);
    }
    fetchRole();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [devsRes, reqsRes] = await Promise.all([
          supabase
            .from('users')
            .select('id, full_name, is_external, roles!inner(name)')
            .eq('active', true)
            .in('roles.name', ['Desarrollador', 'Líder'])
            .order('full_name', { ascending: true }),
          supabase
            .from('requirements')
            .select(`
              *,
              project:projects!project_id(id, name, client_id, client:clients!client_id(id, name)),
              status_rel:status!status_id(id, name, color),
              status_client_rel:status!status_client_id(id, name, color),
              specialization:specializations!specialization_id(id, name),
              billing_type:billing_types!billing_type_id(id, name),
              estimator:users!estimator_user_id(id, full_name),
              leader:users!leader_user_id(id, full_name),
              root:requirements!root_requirement_id(id, reference_id),
              requirement_devs(user_id, user:users!user_id(id, full_name, roles(name)))
            `)
            .not('status_id', 'in', `(${CLOSED_STATUS_IDS.join(',')})`)
            .order('reference_id', { ascending: true }),
        ]);

        if (devsRes.error) throw devsRes.error;
        if (reqsRes.error) throw reqsRes.error;

        if (cancelled) return;
        setDevs(devsRes.data || []);
        setRequirements(reqsRes.data || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error al cargar datos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  /* Keep only requirements whose internal-status color the PO wants shown
     (green / sky blue) — the only statuses that count as "real work".
     Requirements without a matching status color (incl. null status) are
     dropped. Everything downstream — the cards, the active tally and the
     "sin trabajo real" indicator — derives from this filtered set. */
  const visibleRequirements = useMemo(
    () => requirements.filter(
      (r) => VISIBLE_STATUS_COLORS.has((r.status_rel?.color || '').toLowerCase())
    ),
    [requirements]
  );

  /* Group requirements by user_id pulled from the requirement_devs junction. */
  const reqsByDev = useMemo(() => {
    const map = new Map();
    for (const req of visibleRequirements) {
      const links = req.requirement_devs || [];
      for (const link of links) {
        if (!link.user_id) continue;
        if (!map.has(link.user_id)) map.set(link.user_id, []);
        map.get(link.user_id).push(req);
      }
    }
    return map;
  }, [visibleRequirements]);

  const totalActiveReqs = useMemo(() => {
    /* A requirement assigned to N devs counts once for the global tally. */
    const ids = new Set();
    for (const list of reqsByDev.values()) {
      for (const r of list) ids.add(r.id);
    }
    return ids.size;
  }, [reqsByDev]);

  const totalDevs = devs.length;

  /* Render order (Fernando's spec, 2026-07-28): internal devs without real
     work, internal devs with real work, external devs without real work,
     external devs with real work, then Líderes last regardless of work.
     "Real work" reuses the same visible-requirement definition as the
     "sin trabajo real" tally above. `devs` already arrives sorted by
     full_name, and Array.prototype.sort is stable, so alphabetical order is
     preserved within each rank. */
  const sortedDevs = useMemo(() => {
    const rank = (d) => {
      if (d.roles?.name === 'Líder') return 4;
      const hasWork = (reqsByDev.get(d.id) || []).length > 0;
      if (!d.is_external) return hasWork ? 1 : 0;
      return hasWork ? 3 : 2;
    };
    return [...devs].sort((a, b) => rank(a) - rank(b));
  }, [devs, reqsByDev]);

  /* Desarrolladores with no "real work": no visible (green/sky-blue,
     non-closed) requirement assigned. Líderes are excluded from the tally;
     the chips themselves are gated to Gerente/Líder viewers below. Devs with
     zero assignments aren't in reqsByDev at all → counted via the `|| []`.
     Split internal/external per Fernando's request (2026-07-28). */
  const internalDevsWithoutWork = useMemo(
    () => devs.filter(
      (d) => d.roles?.name === 'Desarrollador' && !d.is_external && (reqsByDev.get(d.id) || []).length === 0
    ).length,
    [devs, reqsByDev]
  );

  const externalDevsWithoutWork = useMemo(
    () => devs.filter(
      (d) => d.roles?.name === 'Desarrollador' && d.is_external && (reqsByDev.get(d.id) || []).length === 0
    ).length,
    [devs, reqsByDev]
  );

  /* ── id → reference_id map for the "Root" badge in the detail view.
     Closed roots aren't in this view's rows, so each row's own root join
     supplies the missing entries. ── */
  const rootRefMap = useMemo(() => {
    const map = {};
    for (const r of requirements) {
      map[r.id] = r.reference_id;
      if (r.root?.id) map[r.root.id] = r.root.reference_id;
    }
    return map;
  }, [requirements]);

  const isGerenteOrLider = userRole === 'Gerente' || userRole === 'Líder';
  const selectedRow = selectedReqId != null
    ? requirements.find((r) => r.id === selectedReqId)
    : null;

  function openDetail(reqId) {
    setSelectedReqId(reqId);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  if (selectedRow) {
    return (
      <div className="rqd-page">
        <Navbar />
        <main className="rqd-main">
          <RequirementDetail
            row={selectedRow}
            rootRefMap={rootRefMap}
            isGerenteOrLider={isGerenteOrLider}
            onBack={() => setSelectedReqId(null)}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="rqd-page">
      <Navbar />

      <main className="rqd-main">
        <section className="rqd-header-section">
          <h1 className="rqd-title">Dashboard de Requerimientos</h1>
          <p className="rqd-subtitle">
            Vista semanal de carga de trabajo por desarrollador
          </p>

          <div className="rqd-summary-strip" aria-live="polite">
            <div className="rqd-summary-chip">
              <span className="rqd-summary-icon"><IconUsers /></span>
              <div className="rqd-summary-text">
                <span className="rqd-summary-value">{loading ? '—' : totalDevs}</span>
                <span className="rqd-summary-label">{totalDevs === 1 ? 'Recurso' : 'Recursos'}</span>
              </div>
            </div>
            <div className="rqd-summary-chip">
              <span className="rqd-summary-icon"><IconStack /></span>
              <div className="rqd-summary-text">
                <span className="rqd-summary-value">{loading ? '—' : totalActiveReqs}</span>
                <span className="rqd-summary-label">{totalActiveReqs === 1 ? 'Requerimiento activo' : 'Requerimientos activos'}</span>
              </div>
            </div>
            {isGerenteOrLider && (
              <>
                <div className={`rqd-summary-chip rqd-summary-chip--idle${!loading && internalDevsWithoutWork > 0 ? ' rqd-summary-chip--idle-alert' : ''}`}>
                  <span className="rqd-summary-icon"><IconUserCircle /></span>
                  <div className="rqd-summary-text">
                    <span className="rqd-summary-value">{loading ? '—' : internalDevsWithoutWork}</span>
                    <span className="rqd-summary-label">{internalDevsWithoutWork === 1 ? 'Interno sin trabajo real' : 'Internos sin trabajo real'}</span>
                  </div>
                </div>
                <div className={`rqd-summary-chip rqd-summary-chip--idle${!loading && externalDevsWithoutWork > 0 ? ' rqd-summary-chip--idle-alert' : ''}`}>
                  <span className="rqd-summary-icon"><IconUserCircle /></span>
                  <div className="rqd-summary-text">
                    <span className="rqd-summary-value">{loading ? '—' : externalDevsWithoutWork}</span>
                    <span className="rqd-summary-label">{externalDevsWithoutWork === 1 ? 'Externo sin trabajo real' : 'Externos sin trabajo real'}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {error && (
          <div className="rqd-error" role="alert">
            No se pudieron cargar los datos. {error}
          </div>
        )}

        <section className={`rqd-grid${!loading && devs.length === 1 ? ' rqd-grid--single' : ''}`}>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} index={i} />)
          ) : devs.length === 0 ? (
            <div className="rqd-empty-state">
              <IconInbox />
              <h3>No hay recursos activos</h3>
              <p>No se encontraron usuarios con rol Desarrollador o Líder.</p>
            </div>
          ) : (
            sortedDevs.map((dev, i) => (
              <DeveloperCard
                key={dev.id}
                dev={dev}
                requirements={reqsByDev.get(dev.id) || []}
                index={i}
                onSelectReq={openDetail}
              />
            ))
          )}
        </section>
      </main>
    </div>
  );
}

export default RequirementsDashboard;
